"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import type { ActivityLogParams } from "@/components/profile-activity/ProfileActivityLogs";
import type { ApiRepOverview } from "@/generated/models/ApiRepOverview";
import type { ApiRepCategory } from "@/generated/models/ApiRepCategory";
import type { ApiCicOverview } from "@/generated/models/ApiCicOverview";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { ApiProfileProxyActionType } from "@/generated/models/ApiProfileProxyActionType";
import { amIUser, formatNumberWithCommas } from "@/helpers/Helpers";
import { RateMatter } from "@/types/enums";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useMemo, useState } from "react";
import UserPageIdentityHeaderCICRate from "../identity/header/cic-rate/UserPageIdentityHeaderCICRate";
import UserPageIdentityStatementsAddButton from "../identity/statements/add/UserPageIdentityStatementsAddButton";
import UserPageIdentityStatements from "../identity/statements/UserPageIdentityStatements";
import UserPageRateWrapper from "../utils/rate/UserPageRateWrapper";
import UserCICStatus from "../utils/user-cic-status/UserCICStatus";
import UserCICTypeIcon from "../utils/user-cic-type/UserCICTypeIcon";
import OverlappingAvatars from "@/components/common/OverlappingAvatars";
import UserPageRepModifyModal from "./modify-rep/UserPageRepModifyModal";
import GrantRepDialog from "./new-rep/GrantRepDialog";
import { ArrowDownLeftIcon, ArrowUpRightIcon } from "@heroicons/react/24/solid";
import { getContributorLabel, type RepDirection } from "./UserPageRep.helpers";
import RepCategoryPill from "./RepCategoryPill";
import UserPageCombinedActivityLog from "./UserPageCombinedActivityLog";
import { getCanEditRep } from "./UserPageRep.helpers";
type MobileTab = "rep" | "identity";
function RepEmptyState(t0) {
  const $ = _c(3);
  const {
    loading,
    repDirection
  } = t0;
  if (loading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-mt-4 tw-flex tw-justify-center tw-py-4"><div className="tw-h-5 tw-w-5 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-iron-700 tw-border-t-iron-400" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = repDirection === "given" ? "No rep given yet." : "No rep received yet.";
  let t2;
  if ($[1] !== t1) {
    t2 = <p className="tw-mb-0 tw-mt-4 tw-text-sm tw-font-normal tw-text-iron-500">{t1}</p>;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export default function UserPageRepMobile(t0) {
  const $ = _c(123);
  const {
    profile,
    overview,
    categories,
    cicOverview,
    repDirection,
    onRepDirectionChange,
    initialActivityLogParams,
    loading
  } = t0;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const [activeTab, setActiveTab] = useState("rep");
  const [isGrantRepOpen, setIsGrantRepOpen] = useState(false);
  const [isNicRateOpen, setIsNicRateOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [editCategory, setEditCategory] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setVisibleCount(5);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== categories) {
    t2 = [categories];
    $[1] = categories;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== activeProfileProxy || $[4] !== connectedProfile || $[5] !== profile) {
    t3 = getCanEditRep({
      myProfile: connectedProfile,
      targetProfile: profile,
      activeProfileProxy
    });
    $[3] = activeProfileProxy;
    $[4] = connectedProfile;
    $[5] = profile;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const canEditRep = t3;
  let t4;
  bb0: {
    if (!connectedProfile?.handle) {
      t4 = false;
      break bb0;
    }
    if (activeProfileProxy) {
      if (profile.handle === activeProfileProxy.created_by.handle) {
        t4 = false;
        break bb0;
      }
      let t5;
      if ($[7] !== activeProfileProxy.actions) {
        t5 = activeProfileProxy.actions.some(_temp);
        $[7] = activeProfileProxy.actions;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t4 = t5;
      break bb0;
    }
    if (amIUser({
      profile,
      address
    })) {
      t4 = false;
      break bb0;
    }
    t4 = true;
  }
  const canEditNic = t4;
  let t5;
  if ($[9] !== activeProfileProxy || $[10] !== address || $[11] !== profile) {
    t5 = !activeProfileProxy && !!profile?.handle && (profile.wallets ?? []).some(w => w.wallet.toLowerCase() === address?.toLowerCase());
    $[9] = activeProfileProxy;
    $[10] = address;
    $[11] = profile;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const canEditStatements = t5;
  cicOverview?.contributors.data;
  let t6;
  if ($[13] !== cicOverview?.contributors.data) {
    t6 = (cicOverview?.contributors.data ?? []).slice(0, 3).map(_temp2);
    $[13] = cicOverview?.contributors.data;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const cicAvatarItems = t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setActiveTab("rep");
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = `tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${activeTab === "rep" ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t9 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${activeTab === "rep" ? "tw-border-primary-300/90 tw-bg-transparent tw-ring-2 tw-ring-primary-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t10;
  if ($[16] !== activeTab) {
    t10 = activeTab === "rep" && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-primary-300 tw-transition-all tw-duration-300" />;
    $[16] = activeTab;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t9) {
    t11 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t9}>{t10}</span></div>;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== activeTab) {
    t12 = activeTab === "rep" && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-blue-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-blue-400/20 tw-to-transparent" /></>;
    $[21] = activeTab;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = `tw-relative tw-transition-opacity tw-duration-300 ${activeTab === "rep" ? "" : "tw-opacity-40"}`;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">Total Rep</div>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== overview) {
    t15 = overview ? formatNumberWithCommas(overview.total_rep) : "\u2014";
    $[24] = overview;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15) {
    t16 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-primary-400">{t15}</div>;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== overview || $[29] !== repDirection) {
    t17 = overview && <div className="tw-mt-2.5 tw-flex tw-items-center"><span className="tw-text-xs tw-font-normal tw-text-iron-400">{formatNumberWithCommas(overview.contributor_count)}{" "}{getContributorLabel(repDirection, overview.contributor_count)}</span></div>;
    $[28] = overview;
    $[29] = repDirection;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t13 || $[32] !== t16 || $[33] !== t17) {
    t18 = <div className={t13}>{t14}{t16}{t17}</div>;
    $[31] = t13;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t18 || $[38] !== t8) {
    t19 = <button type="button" onClick={t7} className={t8}>{t11}{t12}{t18}</button>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t18;
    $[38] = t8;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setActiveTab("identity");
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const t21 = `tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-p-4 tw-text-left tw-transition-all tw-duration-300 tw-ease-out ${activeTab === "identity" ? "tw-bg-[#0f1014]" : "tw-bg-white/[0.02]"}`;
  const t22 = `tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-all tw-duration-300 ${activeTab === "identity" ? "tw-border-emerald-300/90 tw-bg-transparent tw-ring-2 tw-ring-emerald-500/25" : "tw-border-iron-400/45 tw-bg-black/10 tw-ring-1 tw-ring-black/20"}`;
  let t23;
  if ($[41] !== activeTab) {
    t23 = activeTab === "identity" && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-emerald-300 tw-transition-all tw-duration-300" />;
    $[41] = activeTab;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t22 || $[44] !== t23) {
    t24 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-3 tw-z-10"><span className={t22}>{t23}</span></div>;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== activeTab) {
    t25 = activeTab === "identity" && <><div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-emerald-500/[0.05] tw-via-transparent tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-iron-300/25 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/40 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /><div className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" /></>;
    $[46] = activeTab;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  const t26 = `tw-relative tw-transition-opacity tw-duration-300 ${activeTab === "identity" ? "" : "tw-opacity-40"}`;
  let t27;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="tw-mb-1.5 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-leading-4 tw-tracking-wider tw-text-iron-500">NIC</div>;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  const t28 = cicOverview?.total_cic ?? profile.cic;
  let t29;
  if ($[49] !== t28) {
    t29 = formatNumberWithCommas(t28);
    $[49] = t28;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== t29) {
    t30 = <div className="tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-white">{t29}</div>;
    $[51] = t29;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  const t31 = cicOverview?.total_cic ?? profile.cic;
  let t32;
  if ($[53] !== t31) {
    t32 = <span className="tw-h-4 tw-w-4 tw-flex-shrink-0"><UserCICTypeIcon cic={t31} /></span>;
    $[53] = t31;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  const t33 = cicOverview?.total_cic ?? profile.cic;
  let t34;
  if ($[55] !== t33) {
    t34 = <span className="tw-text-xs tw-font-semibold tw-uppercase tw-text-emerald-400"><UserCICStatus cic={t33} /></span>;
    $[55] = t33;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  let t35;
  if ($[57] !== t32 || $[58] !== t34) {
    t35 = <div className="tw-mt-2.5 tw-flex tw-items-center tw-gap-1.5">{t32}{t34}</div>;
    $[57] = t32;
    $[58] = t34;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== cicAvatarItems) {
    t36 = cicAvatarItems.length > 0 && <div className="tw-pointer-events-none desktop-hover:tw-pointer-events-auto"><OverlappingAvatars items={cicAvatarItems} size="sm" maxCount={3} /></div>;
    $[60] = cicAvatarItems;
    $[61] = t36;
  } else {
    t36 = $[61];
  }
  const t37 = cicOverview?.contributor_count ?? 0;
  let t38;
  if ($[62] !== t37) {
    t38 = formatNumberWithCommas(t37);
    $[62] = t37;
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  const t39 = (cicOverview?.contributor_count ?? 0) === 1 ? "rater" : "raters";
  let t40;
  if ($[64] !== t38 || $[65] !== t39) {
    t40 = <span className="tw-text-xs tw-font-normal tw-text-iron-400">{t38}{" "}{t39}</span>;
    $[64] = t38;
    $[65] = t39;
    $[66] = t40;
  } else {
    t40 = $[66];
  }
  let t41;
  if ($[67] !== t36 || $[68] !== t40) {
    t41 = <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">{t36}{t40}</div>;
    $[67] = t36;
    $[68] = t40;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== t26 || $[71] !== t30 || $[72] !== t35 || $[73] !== t41) {
    t42 = <div className={t26}>{t27}{t30}{t35}{t41}</div>;
    $[70] = t26;
    $[71] = t30;
    $[72] = t35;
    $[73] = t41;
    $[74] = t42;
  } else {
    t42 = $[74];
  }
  let t43;
  if ($[75] !== t21 || $[76] !== t24 || $[77] !== t25 || $[78] !== t42) {
    t43 = <button type="button" onClick={t20} className={t21}>{t24}{t25}{t42}</button>;
    $[75] = t21;
    $[76] = t24;
    $[77] = t25;
    $[78] = t42;
    $[79] = t43;
  } else {
    t43 = $[79];
  }
  let t44;
  if ($[80] !== t19 || $[81] !== t43) {
    t44 = <div className="tw-grid tw-grid-cols-2 tw-gap-3">{t19}{t43}</div>;
    $[80] = t19;
    $[81] = t43;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== activeTab || $[84] !== canEditNic || $[85] !== canEditRep || $[86] !== canEditStatements || $[87] !== categories || $[88] !== initialActivityLogParams || $[89] !== loading || $[90] !== onRepDirectionChange || $[91] !== profile || $[92] !== repDirection || $[93] !== visibleCount) {
    t45 = <AnimatePresence mode="wait">{activeTab === "rep" ? <motion.div key="rep" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: "easeInOut"
      }}><div className="tw-mt-4 tw-flex tw-items-center tw-gap-4"><button type="button" aria-pressed={repDirection === "received"} onClick={() => onRepDirectionChange("received")} className={`tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-medium tw-transition-colors tw-duration-200 ${repDirection === "received" ? "tw-text-iron-100" : "tw-text-iron-500 hover:tw-text-iron-300"}`}><ArrowDownLeftIcon className="tw-h-3 tw-w-3 tw-flex-shrink-0" aria-hidden="true" />Received</button><button type="button" aria-pressed={repDirection === "given"} onClick={() => onRepDirectionChange("given")} className={`tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-medium tw-transition-colors tw-duration-200 ${repDirection === "given" ? "tw-text-iron-100" : "tw-text-iron-500 hover:tw-text-iron-300"}`}><ArrowUpRightIcon className="tw-h-3 tw-w-3 tw-flex-shrink-0" aria-hidden="true" />Given</button></div>{categories.length > 0 && <div className="tw-mt-4"><div className="tw-mb-3 tw-whitespace-nowrap tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-100">Rep Categories</div><div className="tw-flex tw-flex-wrap tw-gap-2">{categories.slice(0, visibleCount).map(cat => <RepCategoryPill key={cat.category} category={cat} canEdit={canEditRep && repDirection === "received"} onEdit={setEditCategory} direction={repDirection} compact={true} />)}{categories.length > visibleCount && <button type="button" onClick={() => setVisibleCount(_temp3)} className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700/60 tw-bg-iron-900/60 tw-px-4 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-iron-400 tw-transition-colors hover:tw-border-iron-600/60 hover:tw-bg-iron-800/60 hover:tw-text-iron-300">+{categories.length - visibleCount} more</button>}</div></div>}{categories.length === 0 && <RepEmptyState loading={loading} repDirection={repDirection} />}{canEditRep && repDirection === "received" && <div className="tw-mt-4"><UserPageRateWrapper profile={profile} type={RateMatter.REP} hideOwnProfileMessage={true}><div className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-blue-500/20 tw-bg-blue-400/5 tw-px-5 tw-py-3"><span className="tw-text-xs tw-font-medium tw-text-blue-300/70">Add rep to this identity</span><button onClick={() => setIsGrantRepOpen(true)} className="tw-flex tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-solid tw-border-primary-500 tw-bg-primary-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-blue-500/20 tw-transition tw-duration-300 tw-ease-out hover:tw-border-primary-600 hover:tw-bg-primary-600 md:tw-py-3"><svg className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>Grant Rep</button></div></UserPageRateWrapper></div>}<div className="tw-mt-6"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} matter={RateMatter.REP} withMatterFilter={false} /></div></motion.div> : <motion.div key="identity" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease: "easeInOut"
      }}>{canEditNic && <div className="tw-mt-4"><UserPageRateWrapper profile={profile} type={RateMatter.NIC} hideOwnProfileMessage={true}><div className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-emerald-500/10 tw-bg-emerald-500/5 tw-px-5 tw-py-3"><span className="tw-text-xs tw-font-medium tw-text-emerald-200/70">Verify this identity</span><button onClick={() => setIsNicRateOpen(true)} className="tw-flex tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-emerald-500 tw-bg-emerald-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-emerald-500/20 tw-transition tw-duration-300 tw-ease-out hover:tw-border-emerald-400 hover:tw-bg-emerald-400 sm:tw-py-3">Rate NIC</button></div></UserPageRateWrapper></div>}<div className="tw-mt-6 tw-flex tw-items-center tw-justify-between"><h3 className="tw-mb-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-iron-100">ID Statements</h3>{canEditStatements && <UserPageIdentityStatementsAddButton profile={profile} />}</div><div className="tw-mt-3 tw-rounded-xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#0f1014]"><UserPageIdentityStatements profile={profile} /></div><div className="tw-mt-6"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} matter={RateMatter.NIC} withMatterFilter={false} /></div></motion.div>}</AnimatePresence>;
    $[83] = activeTab;
    $[84] = canEditNic;
    $[85] = canEditRep;
    $[86] = canEditStatements;
    $[87] = categories;
    $[88] = initialActivityLogParams;
    $[89] = loading;
    $[90] = onRepDirectionChange;
    $[91] = profile;
    $[92] = repDirection;
    $[93] = visibleCount;
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = () => setIsGrantRepOpen(false);
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] !== isGrantRepOpen || $[97] !== overview || $[98] !== profile) {
    t47 = <GrantRepDialog profile={profile} overview={overview} isOpen={isGrantRepOpen} onClose={t46} />;
    $[96] = isGrantRepOpen;
    $[97] = overview;
    $[98] = profile;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  let t48;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = () => setIsNicRateOpen(false);
    $[100] = t48;
  } else {
    t48 = $[100];
  }
  let t49;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = () => setIsNicRateOpen(false);
    $[101] = t49;
  } else {
    t49 = $[101];
  }
  let t50;
  if ($[102] !== profile) {
    t50 = <UserPageIdentityHeaderCICRate profile={profile} isTooltip={false} onSuccess={t49} />;
    $[102] = profile;
    $[103] = t50;
  } else {
    t50 = $[103];
  }
  let t51;
  if ($[104] !== profile || $[105] !== t50) {
    t51 = <UserPageRateWrapper profile={profile} type={RateMatter.NIC}>{t50}</UserPageRateWrapper>;
    $[104] = profile;
    $[105] = t50;
    $[106] = t51;
  } else {
    t51 = $[106];
  }
  let t52;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <div className="tw-mt-3"><button onClick={() => setIsNicRateOpen(false)} type="button" className="tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button></div>;
    $[107] = t52;
  } else {
    t52 = $[107];
  }
  let t53;
  if ($[108] !== t51) {
    t53 = <div className="tw-px-4 sm:tw-px-6">{t51}{t52}</div>;
    $[108] = t51;
    $[109] = t53;
  } else {
    t53 = $[109];
  }
  let t54;
  if ($[110] !== isNicRateOpen || $[111] !== t53) {
    t54 = <MobileWrapperDialog title="Rate NIC" isOpen={isNicRateOpen} onClose={t48} tabletModal={true} maxWidthClass="md:tw-max-w-md">{t53}</MobileWrapperDialog>;
    $[110] = isNicRateOpen;
    $[111] = t53;
    $[112] = t54;
  } else {
    t54 = $[112];
  }
  let t55;
  if ($[113] !== canEditRep || $[114] !== editCategory || $[115] !== profile) {
    t55 = canEditRep && editCategory && <UserPageRepModifyModal profile={profile} category={editCategory} onClose={() => setEditCategory(null)} />;
    $[113] = canEditRep;
    $[114] = editCategory;
    $[115] = profile;
    $[116] = t55;
  } else {
    t55 = $[116];
  }
  let t56;
  if ($[117] !== t44 || $[118] !== t45 || $[119] !== t47 || $[120] !== t54 || $[121] !== t55) {
    t56 = <div>{t44}{t45}{t47}{t54}{t55}</div>;
    $[117] = t44;
    $[118] = t45;
    $[119] = t47;
    $[120] = t54;
    $[121] = t55;
    $[122] = t56;
  } else {
    t56 = $[122];
  }
  return t56;
}
function _temp3(prev) {
  return prev + 10;
}
function _temp2(c) {
  return {
    key: c.profile.handle ?? c.profile.primary_address,
    pfpUrl: c.profile.pfp ?? null,
    ariaLabel: c.profile.handle ?? c.profile.primary_address,
    fallback: c.profile.handle ? c.profile.handle.charAt(0).toUpperCase() : "?",
    title: c.profile.handle ?? c.profile.primary_address,
    tooltipContent: <span>{c.profile.handle ?? c.profile.primary_address} ·{" "}{formatNumberWithCommas(c.contribution)}</span>
  };
}
function _temp(action) {
  return action.action_type === ApiProfileProxyActionType.AllocateCic;
}